import React, { useState } from "react";
import { ArrowLeft, BadgeHelp, Headset, Info, Shield, Star, UserX } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import logo from "../logo.svg";

const WHATSAPP_NUMBER = "56928232048";
const WHATSAPP_BASE_URL = `https://wa.me/${WHATSAPP_NUMBER}`;

const infoConfig = {
  "quienes-somos": { title: "Quiénes somos", icon: Info },
  "preguntas-frecuentes": { title: "Preguntas frecuentes", icon: BadgeHelp },
  "politica-privacidad": { title: "Política de privacidad", icon: Shield },
  recomendaciones: { title: "Recomendaciones", icon: Star }
};

const frequentQuestions = [
  {
    question: "¿Qué hace Cynaxia Spa?",
    answer:
      "Desarrollamos software a medida, apps móviles, plataformas web y soluciones de automatización para empresas, profesionales y emprendedores."
  },
  {
    question: "¿Trabajan con clientes de cualquier rubro?",
    answer:
      "Sí. Nos adaptamos a salud, educación, comercio, logística y otros sectores, aplicando buenas prácticas técnicas y de negocio."
  },
  {
    question: "¿Cómo inicia un proyecto con ustedes?",
    answer:
      "Comenzamos con una reunión de descubrimiento para entender objetivos, alcance, tiempos y prioridades. Luego proponemos una ruta de trabajo."
  },
  {
    question: "¿Entregan cotización formal?",
    answer:
      "Sí. Entregamos propuesta técnica y económica, con alcances, exclusiones, plazos estimados y condiciones comerciales."
  },
  {
    question: "¿Pueden mejorar un sistema ya existente?",
    answer:
      "Sí. Realizamos auditorías de código, modernización, corrección de errores, optimización de rendimiento y escalabilidad."
  },
  {
    question: "¿Publican aplicaciones en Google Play?",
    answer:
      "Sí. Apoyamos el proceso de publicación y actualización cumpliendo requisitos técnicos y de políticas de la plataforma."
  },
  {
    question: "¿Ofrecen soporte después de la entrega?",
    answer:
      "Sí. Tenemos planes de soporte, mantenimiento correctivo/evolutivo y monitoreo según necesidad del cliente."
  },
  {
    question: "¿Qué tecnologías utilizan?",
    answer:
      "Seleccionamos tecnologías según el caso: web moderna, mobile cross-platform o nativo, backend escalable e integraciones API."
  },
  {
    question: "¿Pueden integrar pagos, WhatsApp, ERP o CRM?",
    answer:
      "Sí. Implementamos integraciones con pasarelas de pago, herramientas de mensajería, sistemas internos y plataformas externas."
  },
  {
    question: "¿Cómo protegen los datos de usuarios?",
    answer:
      "Aplicamos principios de seguridad por diseño: control de acceso, mínimos privilegios, cifrado en tránsito, trazabilidad y resguardo operacional."
  },
  {
    question: "¿Manejan acuerdos de confidencialidad?",
    answer:
      "Sí. Podemos operar con NDA y cláusulas de confidencialidad para proteger información sensible del negocio."
  },
  {
    question: "¿Cuánto tarda un desarrollo?",
    answer:
      "Depende del alcance. Un MVP puede tardar semanas; plataformas complejas requieren etapas incrementales con hitos definidos."
  },
  {
    question: "¿Puedo solicitar eliminación de mi cuenta?",
    answer:
      "Sí. Disponemos de un canal en Soporte Técnico para solicitar la eliminación de cuenta con validación de identidad."
  },
  {
    question: "¿Cómo reporto un incidente técnico?",
    answer:
      "Desde Soporte Técnico por email o WhatsApp, incluyendo descripción, evidencia y hora del incidente para priorizar correctamente."
  },
  {
    question: "¿Entregan documentación y capacitación?",
    answer:
      "Sí. Entregamos documentación funcional/técnica y capacitación operativa según el alcance contratado."
  }
];

const InfoPage = () => {
  const { infoId } = useParams();
  const section = infoConfig[infoId];
  const [feedbackForm, setFeedbackForm] = useState({ name: "", product: "", comment: "" });
  const [publishedCount, setPublishedCount] = useState(0);
  const [deletionForm, setDeletionForm] = useState({
    productName: "",
    productType: "app",
    email: "",
    username: ""
  });

  const updateDeletionForm = (key, value) => {
    setDeletionForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateFeedbackForm = (key, value) => {
    setFeedbackForm((prev) => ({ ...prev, [key]: value }));
  };

  const addComment = (event) => {
    event.preventDefault();
    const name = feedbackForm.name.trim();
    const product = feedbackForm.product.trim();
    const comment = feedbackForm.comment.trim();

    if (!name || !product || !comment) return;

    setFeedbackForm({ name: "", product: "", comment: "" });
    setPublishedCount((prev) => prev + 1);
  };

  const renderSectionContent = () => {
    if (infoId === "quienes-somos") {
      return (
        <div className="space-y-4 text-slate-700 dark:text-slate-200">
          <p>
            <strong>Cynaxia Spa</strong> es una empresa especialista en desarrollo de software que diseña, construye y opera
            soluciones digitales para organizaciones que buscan crecer con tecnología confiable.
          </p>
          <p>
            Nuestro foco está en convertir desafíos reales de negocio en productos digitales de alto valor: aplicaciones
            móviles, plataformas web, software interno y automatizaciones que mejoran productividad, trazabilidad y atención
            al cliente.
          </p>
          <p>
            Trabajamos con enfoque integral: entendimiento estratégico, arquitectura técnica, desarrollo ágil, aseguramiento
            de calidad, despliegue y soporte evolutivo. Esto permite que cada proyecto no solo funcione, sino que también sea
            sostenible en el tiempo.
          </p>
          <p>
            En Cynaxia priorizamos la seguridad, la claridad con el cliente y la mejora continua. Nuestro compromiso es
            entregar soluciones estables, escalables y alineadas a objetivos medibles.
          </p>
        </div>
      );
    }

    if (infoId === "preguntas-frecuentes") {
      return (
        <div className="space-y-4">
          {frequentQuestions.map((faq) => (
            <div key={faq.question} className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800">
              <p className="font-semibold text-slate-900 dark:text-slate-100">{faq.question}</p>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{faq.answer}</p>
            </div>
          ))}
        </div>
      );
    }

    if (infoId === "politica-privacidad") {
      return (
        <div className="space-y-5 text-slate-700 dark:text-slate-200">
          <p>
            En Cynaxia Spa tratamos los datos personales de forma lícita, transparente y proporcional, con medidas técnicas y
            organizativas razonables para proteger su confidencialidad, integridad y disponibilidad.
          </p>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800">
            <p className="mb-2 font-semibold">1) Tratamiento de datos y base legal</p>
            <p className="text-sm">
              Recopilamos datos de contacto, uso y soporte para prestar servicios, responder solicitudes, mejorar productos,
              cumplir obligaciones legales y prevenir fraudes o abusos. El tratamiento se fundamenta en consentimiento,
              ejecución contractual e interés legítimo según corresponda.
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800">
            <p className="mb-2 font-semibold">2) Política para apps distribuidas en Google Play</p>
            <p className="text-sm">
              Nuestras aplicaciones buscan ajustarse a los lineamientos vigentes de Google Play Console respecto de permisos,
              seguridad, datos de usuario, disclosure de prácticas de recopilación y uso responsable de APIs. Cualquier
              funcionalidad sensible se implementa bajo principio de minimización de datos y propósito específico.
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800">
            <p className="mb-2 font-semibold">3) Descargo de responsabilidad por uso inadecuado</p>
            <p className="text-sm">
              Cynaxia Spa no se responsabiliza por daños derivados del uso inadecuado, contrario a las instrucciones, ilícito o
              no autorizado de sus productos y servicios por parte de terceros o usuarios finales. Cada usuario es responsable
              de operar la plataforma conforme a la normativa aplicable y buenas prácticas de seguridad.
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800">
            <p className="mb-2 font-semibold">4) Retención, transferencias y terceros</p>
            <p className="text-sm">
              Conservamos la información por el tiempo necesario para cumplir finalidades declaradas, exigencias legales y
              defensa de intereses legítimos. Cuando intervienen proveedores externos, exigimos estándares razonables de
              seguridad y confidencialidad.
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800">
            <p className="mb-2 font-semibold">5) Derechos de los titulares</p>
            <p className="text-sm">
              Puedes solicitar acceso, rectificación, actualización, eliminación u oposición al tratamiento de datos, sujeto a
              validación de identidad y restricciones legales aplicables.
            </p>
          </div>
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 dark:border-amber-700/60 dark:bg-amber-500/10">
            <p className="text-sm">
              Esta política se complementa con los términos contractuales y puede actualizarse para reflejar cambios normativos,
              operativos o regulatorios, reduciendo vacíos legales y fortaleciendo la protección de la empresa y sus usuarios.
            </p>
          </div>
          <p className="text-sm">
            Versión detallada adicional:{" "}
            <Link to="/privacy" className="font-semibold text-amber-600 underline hover:text-amber-500 dark:text-amber-300">
              Ir a Política de privacidad completa
            </Link>
          </p>
        </div>
      );
    }

    if (infoId === "recomendaciones") {
      return (
        <div className="space-y-6">
          <p className="text-slate-700 dark:text-slate-200">
            Recomendamos definir objetivos concretos, usuarios clave y prioridades del proyecto antes de iniciar desarrollo. Tu
            feedback nos ayuda a mejorar continuamente.
          </p>

          <form onSubmit={addComment} className="grid gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 md:grid-cols-2 dark:border-slate-700 dark:bg-slate-800">
            <input
              type="text"
              value={feedbackForm.name}
              onChange={(event) => updateFeedbackForm("name", event.target.value)}
              placeholder="Tu nombre"
              className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-amber-500 dark:border-slate-600 dark:bg-slate-900"
            />
            <input
              type="text"
              value={feedbackForm.product}
              onChange={(event) => updateFeedbackForm("product", event.target.value)}
              placeholder="Producto o servicio"
              className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-amber-500 dark:border-slate-600 dark:bg-slate-900"
            />
            <textarea
              rows={3}
              value={feedbackForm.comment}
              onChange={(event) => updateFeedbackForm("comment", event.target.value)}
              placeholder="Escribe tu comentario"
              className="md:col-span-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-amber-500 dark:border-slate-600 dark:bg-slate-900"
            />
            <button
              type="submit"
              className="md:col-span-2 inline-flex justify-center rounded-xl bg-amber-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-amber-400 dark:text-slate-950"
            >
              Publicar comentario
            </button>
          </form>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Gracias por tu aporte. Comentarios enviados: <strong>{publishedCount}</strong>. Los testimonios se visualizan en la
            página principal, debajo de las cards de servicios.
          </p>
        </div>
      );
    }

    return null;
  };

  if (infoId === "soporte-tecnico") {
    return (
      <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
        <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur dark:border-slate-800 dark:bg-slate-950/95">
          <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-5">
            <Link to="/" className="flex items-center gap-3">
              <img src={logo} alt="Logo Cynaxia" className="h-9 w-9" />
              <span className="text-lg font-bold tracking-wide text-amber-500 dark:text-amber-400">Cynaxia</span>
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700 transition hover:border-amber-500 hover:text-amber-700 dark:border-slate-700 dark:text-slate-100 dark:hover:border-amber-400 dark:hover:text-amber-300"
            >
              <ArrowLeft className="h-4 w-4" />
              Volver
            </Link>
          </div>
        </header>

        <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-20">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-900">
            <div className="mb-6 flex items-center gap-3">
              <Headset className="h-6 w-6 text-amber-500 dark:text-amber-300" />
              <h1 className="text-2xl font-bold">Soporte técnico</h1>
            </div>
            <div className="space-y-2 text-slate-700 dark:text-slate-200">
              <p>
                Contacto por correo:{" "}
                <a className="text-amber-600 underline dark:text-amber-300" href="mailto:soporte@cynaxia.cl">
                  soporte@cynaxia.cl
                </a>
              </p>
              <p>
                Contacto por WhatsApp:{" "}
                <a className="text-amber-600 underline dark:text-amber-300" href={WHATSAPP_BASE_URL} target="_blank" rel="noreferrer">
                  +56 9 2823 2048
                </a>
              </p>
            </div>

            <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-700 dark:bg-slate-800">
              <div className="mb-4 flex items-center gap-2">
                <UserX className="h-5 w-5 text-red-400" />
                <h2 className="text-lg font-semibold">Solicitud de eliminación de cuenta</h2>
              </div>
              <p className="mb-4 text-sm text-slate-600 dark:text-slate-300">
                Completa este formulario indicando el producto, tipo de suscripción, email y nombre de usuario con el que te
                registraste. El plazo máximo de respuesta para esta solicitud es de 15 días.
              </p>
              <form className="grid gap-3 md:grid-cols-2">
                <input
                  type="text"
                  value={deletionForm.productName}
                  onChange={(event) => updateDeletionForm("productName", event.target.value)}
                  placeholder="Nombre del producto"
                  className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-amber-500 dark:border-slate-600 dark:bg-slate-900"
                />
                <select
                  value={deletionForm.productType}
                  onChange={(event) => updateDeletionForm("productType", event.target.value)}
                  className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-amber-500 dark:border-slate-600 dark:bg-slate-900"
                >
                  <option value="app">App</option>
                  <option value="web">Web</option>
                </select>
                <input
                  type="email"
                  value={deletionForm.email}
                  onChange={(event) => updateDeletionForm("email", event.target.value)}
                  placeholder="Email de suscripción"
                  className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-amber-500 dark:border-slate-600 dark:bg-slate-900"
                />
                <input
                  type="text"
                  value={deletionForm.username}
                  onChange={(event) => updateDeletionForm("username", event.target.value)}
                  placeholder="Nombre de usuario"
                  className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-amber-500 dark:border-slate-600 dark:bg-slate-900"
                />
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!section) {
    return (
      <div className="min-h-screen bg-slate-50 px-6 py-20 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
        <div className="mx-auto max-w-4xl rounded-2xl border border-slate-200 bg-white p-8 dark:border-slate-700 dark:bg-slate-900">
          <h1 className="text-2xl font-bold">Sección no encontrada</h1>
          <Link to="/" className="mt-4 inline-block text-amber-600 underline dark:text-amber-300">
            Volver al inicio
          </Link>
        </div>
      </div>
    );
  }

  const Icon = section.icon;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur dark:border-slate-800 dark:bg-slate-950/95">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-5">
          <Link to="/" className="flex items-center gap-3">
            <img src={logo} alt="Logo Cynaxia" className="h-9 w-9" />
            <span className="text-lg font-bold tracking-wide text-amber-500 dark:text-amber-400">Cynaxia</span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700 transition hover:border-amber-500 hover:text-amber-700 dark:border-slate-700 dark:text-slate-100 dark:hover:border-amber-400 dark:hover:text-amber-300"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver
          </Link>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-20">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-900">
          <div className="mb-6 flex items-center gap-3">
            <Icon className="h-6 w-6 text-amber-500 dark:text-amber-300" />
            <h1 className="text-2xl font-bold">{section.title}</h1>
          </div>
          {renderSectionContent()}
        </div>
      </div>
    </div>
  );
};

export default InfoPage;
